import React, { useRef, useState } from 'react';
import { AnimatePresence, motion, useInView } from 'framer-motion';
import { CheckIcon, HeartIcon, Loader2Icon } from 'lucide-react';
import { toast } from 'sonner';
import { useCart } from '../../contexts/CartContext';
import { useWishlist } from '../../contexts/WishlistContext';
import { formatPrice } from '../../utils/currency';
import { easeOut } from '../../utils/motion';
import type { Product } from '../../types/catalog';
import { QuantityStepper } from '../ui/QuantityStepper';
type AddState = 'idle' | 'adding' | 'added';
export function AddToCartPanel({
  product
}: {
  product: Product;
}) {
  const {
    addItem
  } = useCart();
  const {
    has,
    toggle
  } = useWishlist();
  const [option, setOption] = useState<string | undefined>(product.options && product.options.values.length === 1 ? product.options.values[0] : undefined);
  const [quantity, setQuantity] = useState(1);
  const [state, setState] = useState<AddState>('idle');
  const [optionError, setOptionError] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);
  const inView = useInView(buttonRef, {
    margin: '0px 0px -40px 0px'
  });
  const liked = has(product.id);
  const handleAdd = () => {
    if (product.options && !option) {
      setOptionError(true);
      return;
    }
    if (state !== 'idle') return;
    setState('adding');
    window.setTimeout(() => {
      addItem(product.id, quantity, option);
      setState('added');
      window.setTimeout(() => setState('idle'), 1600);
    }, 380);
  };
  const onWishlist = () => {
    const added = toggle(product.id);
    toast(added ? 'Ajouté à vos favoris' : 'Retiré de vos favoris', {
      description: product.name
    });
  };
  const buttonLabel = <AnimatePresence mode="wait" initial={false} data-id="element-508" data-mp="2dizmdebjxbgj">
      <motion.span key={state} initial={{
      opacity: 0,
      y: 6
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: -6
    }} transition={{
      duration: 0.15,
      ease: easeOut
    }} className="flex items-center gap-2" data-id="element-509" data-mp="3jy8z3mztlj3l">
        {state === 'idle' && <>Ajouter au panier · {formatPrice(product.price * quantity)}</>}
        {state === 'adding' && <>
            <Loader2Icon className="h-4 w-4 animate-spin" data-id="element-510" data-mp="3973zzwufab8z" /> Ajout…
          </>}
        {state === 'added' && <>
            <CheckIcon className="h-4 w-4" data-id="element-511" data-mp="349m2d2luviga" /> Ajouté au panier
          </>}
      </motion.span>
    </AnimatePresence>;
  const buttonClass = `flex h-14 flex-1 items-center justify-center rounded-full text-sm font-medium text-paper transition-[background-color,transform] duration-150 ease-out active:scale-[0.98] ${state === 'added' ? 'bg-sage' : 'bg-ink hover:bg-clay'}`;
  return <div data-id="element-512" data-mp="2lh1drxd6vzia">
      {product.options && <fieldset className="mb-6" data-id="element-513" data-mp="2lfyotuzfg750">
          <legend className="mb-3 flex w-full items-baseline justify-between text-sm" data-id="element-514" data-mp="1xrfdf6l2ze8p">
            <span data-id="element-515" data-mp="1stnnuj7ws47o">
              {product.options.label}
              {option && <span className="text-muted" data-id="element-516" data-mp="1srb5i4y8nru3"> : {option}</span>}
            </span>
          </legend>
          <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={product.options.label} data-id="element-517" data-mp="89a2l87zkukw">
            {product.options.values.map(v => <button key={v} type="button" role="radio" aria-checked={option === v} onClick={() => {
          setOption(v);
          setOptionError(false);
        }} className={`h-11 min-w-[52px] rounded-full border px-4 text-sm transition-colors duration-150 ${option === v ? 'border-ink bg-ink text-paper' : 'border-line bg-card hover:border-ink'}`} data-id="element-518" data-mp="3ujmgtl7bgldx">
                {v}
              </button>)}
          </div>
          <AnimatePresence data-id="element-519" data-mp="32qca2f2bbako">
            {optionError && <motion.p initial={{
          opacity: 0,
          y: -4
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.18
        }} className="mt-2 text-xs text-clay" role="alert" data-id="element-520" data-mp="36sh2xbkxs369">
                Choisissez d’abord {product.options.label.toLowerCase() === 'taille' ? 'une taille' : 'une option'}.
              </motion.p>}
          </AnimatePresence>
        </fieldset>}

      <p className="mb-3 flex items-center gap-2 text-sm" data-id="element-521" data-mp="9tv9hyj89asq">
        <span className={`h-2 w-2 rounded-full ${product.stock <= 3 ? 'bg-clay' : 'bg-sage'}`} aria-hidden data-id="element-522" data-mp="9r90rvklop8x" />
        {product.stock === 1 ? 'Pièce unique — un seul exemplaire' : product.stock <= 3 ? `Plus que ${product.stock} en stock` : 'En stock, prêt à partir'}
      </p>

      <div ref={buttonRef} className="flex items-center gap-3" data-id="element-523" data-mp="2klenlx9v1wi5">
        {product.stock > 1 && <QuantityStepper value={quantity} max={product.stock} onChange={setQuantity} data-id="element-524" data-mp="2gjt2xpr34dxe" />}
        <button type="button" onClick={handleAdd} className={buttonClass} aria-live="polite" data-id="element-525" data-mp="w1qxtsdg7z8h">
          {buttonLabel}
        </button>
        <button type="button" onClick={onWishlist} aria-pressed={liked} aria-label={liked ? 'Retirer des favoris' : 'Ajouter aux favoris'} className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-line transition-[border-color,transform] duration-150 hover:border-ink active:scale-95" data-id="element-526" data-mp="102khuanrstw3">
          <HeartIcon className={`h-5 w-5 ${liked ? 'fill-clay text-clay' : ''}`} data-id="element-527" data-mp="1jr368q0yc39q" />
        </button>
      </div>

      <AnimatePresence data-id="element-528" data-mp="1nsonsoofl5q9">
        {!inView && <motion.div initial={{
        y: '100%'
      }} animate={{
        y: 0
      }} exit={{
        y: '100%'
      }} transition={{
        duration: 0.25,
        ease: easeOut
      }} className="fixed inset-x-0 bottom-0 z-30 flex items-center gap-3 border-t border-line bg-paper px-4 py-3 md:hidden" data-id="element-529" data-mp="1nqu1b5xih5k2">
            <div className="min-w-0 flex-1" data-id="element-530" data-mp="11owaqu8gwtv2">
              <p className="truncate text-sm font-medium" data-id="element-531" data-mp="11pp8j5pg8912">{product.name}</p>
              <p className="text-xs text-muted" data-id="element-532" data-mp="wqckwiqr6xxf">{option ? `${product.options?.label} ${option} · ` : ''}{formatPrice(product.price)}</p>
            </div>
            <button type="button" onClick={() => {
          if (product.options && !option) {
            buttonRef.current?.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
            setOptionError(true);
            return;
          }
          handleAdd();
        }} className={`flex h-11 shrink-0 items-center justify-center rounded-full px-5 text-sm font-medium text-paper ${state === 'added' ? 'bg-sage' : 'bg-ink'}`} data-id="element-533" data-mp="1anhomc2zvkpi">
              {state === 'added' ? 'Ajouté' : state === 'adding' ? 'Ajout…' : 'Ajouter'}
            </button>
          </motion.div>}
      </AnimatePresence>
    </div>;
}